import { Fragment, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import type { SentinelTicketMetrics } from '@/types/sentinelAnalytics';
import { AnalyticsTable, Cell, EdgeCell, EdgeHead, ExpandToggle, GroupHead, Head, STICKY_CELL, STICKY_HEAD, SUB_ROW_NAME } from './analytics-table';
import { subRowProps, useExpandedRows } from './use-expanded-rows';
import { useSortedRows } from './use-sorted-rows';
import { formatCount } from './format';
import { Legend } from './legend';
import { MiniSplit } from './mini-split';
import { RateCell } from './rate-cell';

type SortKey = 'name' | 'handled' | 'automation';
/** One table row: a category or a channel, with a category's sub-categories as `subRows`. */
type Row = { name: string; tickets: SentinelTicketMetrics; subRows?: Row[] };

function sortValue(row: Row, key: SortKey): string | number | null {
  if (key === 'name') return row.name;
  return key === 'handled' ? row.tickets.handled.rate : row.tickets.automation.rate;
}

interface TicketMetricsTableProps {
  /** First column header, e.g. "Category Name". */
  nameLabel: string;
  rows: Row[];
}

function TicketRow({ row: { name, tickets }, name: label, sub = false, closing = false }: { row: Row; name: ReactNode; sub?: boolean; closing?: boolean }) {
  return (
    <tr {...(sub ? subRowProps(closing) : {})}>
      <EdgeCell sticky />
      <Cell divider={false} className={cn(STICKY_CELL, 'border-r border-r-gray-500 font-medium', sub && SUB_ROW_NAME)}>{label}</Cell>
      <Cell divider={false} className='pl-3'>{formatCount(tickets.totalTickets)} {tickets.totalTickets === 1 ? 'Ticket' : 'Tickets'}</Cell>
      <Cell divider={false}>
        <MiniSplit left={tickets.inScopeTickets} right={tickets.outOfScopeTickets} label={`${name}: in scope vs out of scope`} />
      </Cell>
      <Cell><RateCell metric={tickets.handled} gap='tight' /></Cell>
      <Cell><RateCell metric={tickets.unassignment} gap='tight' /></Cell>
      <Cell>{formatCount(tickets.missedOpportunity.count)} {tickets.missedOpportunity.count === 1 ? 'ticket' : 'tickets'}</Cell>
      <Cell><RateCell metric={tickets.automation} gap='tight' /></Cell>
      <EdgeCell />
    </tr>
  );
}

export function TicketMetricsTable({ nameLabel, rows }: TicketMetricsTableProps) {
  const { sorted, sortRows, sortProps } = useSortedRows(rows, sortValue);
  const { isExpanded, isClosing, toggle } = useExpandedRows();
  return (
    <AnalyticsTable>
      <thead>
        <tr>
          <EdgeHead rowSpan={2} sticky />
          <Head rowSpan={2} divider={false} className={cn(STICKY_HEAD, 'w-60 border-r border-r-gray-500')} sort={sortProps('name')}>{nameLabel}</Head>
          <GroupHead label='Ticket Volume' colSpan={2} className='pl-3' />
          <Head rowSpan={2} sort={sortProps('handled')}>Tickets Handled</Head>
          <GroupHead label='Leakage' colSpan={2} />
          <Head rowSpan={2} sort={sortProps('automation')}>Automation Rate</Head>
          <EdgeHead rowSpan={2} />
        </tr>
        <tr>
          <Head edge='bottom' divider={false} className='pt-[3px] pb-1 pl-[22px]'>Total</Head>
          <Head edge='bottom' divider={false} className='pt-[3px] pb-1'>
            <span className='sr-only'>In-scope vs out of scope</span>
            <Legend items={[{ label: 'In-Scope', swatch: 'bg-indigo-700' }, { label: 'Out of scope', swatch: 'bg-indigo-200' }]} className='ml-auto' />
          </Head>
          <Head edge='bottom' divider={false} className='pt-[3px] pb-1 pl-2.5'>Un-Assignment Rate</Head>
          <Head edge='bottom' className='pt-[3px] pb-1'>Missed Opportunity</Head>
        </tr>
      </thead>
      <tbody>
        {sorted.map((row) => (
          <Fragment key={row.name}>
            <TicketRow row={row} name={row.subRows?.length ? <ExpandToggle name={row.name} expanded={isExpanded(row.name)} onToggle={() => toggle(row.name)} /> : row.name} />
            {row.subRows && (isExpanded(row.name) || isClosing(row.name)) ? sortRows(row.subRows).map((sub) => <TicketRow key={`${row.name}/${sub.name}`} row={sub} name={sub.name} sub closing={isClosing(row.name)} />) : null}
          </Fragment>
        ))}
      </tbody>
    </AnalyticsTable>
  );
}
